import { Checklist, Receipt, ReceiptLong } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DiscountIcon from '@mui/icons-material/Discount';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import FormatListNumberedRtlIcon from '@mui/icons-material/FormatListNumberedRtl';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeIcon from '@mui/icons-material/Home';
import PaymentsIcon from '@mui/icons-material/Payments';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { CartContext } from '../contexts/cart-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { usePermissions } from '../hooks/use-permissions';
import { useSessionActions } from '../hooks/use-session-actions';
import { CartData } from '../routes/leads-map/data';
import { AWS_GROUP } from '../types';
import { isAgt } from '../utils/is-agent';
import {
  AllianceAppBar,
  AllianceAppBarSpacer,
} from './alliance/alliance-app-bar';
import {
  AppBanners,
  ConnectionBanner,
  MaintenanceBanner,
  UpdatedBanner,
} from './alliance/app-banners';
import {
  AppNavDrawer,
  SIDEBAR_COLORS,
  SIDEBAR_WIDTH,
} from './alliance/app-nav-drawer';
import { CURRENT_APP } from './alliance/current-app';
import { SessionExpiredDialog } from './alliance/session-expired-dialog';
import { UserControl } from './alliance/user-control';
import { Cart } from './cart';
import { CartButton } from './cart/cart-button';
import { RouteErrorBoundary } from './route-container/route-error-boundary';
import { SelectAgentImpersonate } from './select-agent-impersonate';
import { useCartAgent } from '../hooks/use-cart-agent';

interface NavItem {
  name: string;
  icon: JSX.Element;
  path?: string;
  // External link, opened in a new tab
  href?: string;
  children?: NavItem[];
  // Whether or not this link should be hidden from production
  hideFromProd?: boolean;
  // Only this Group can see this link
  hasGroup?: AWS_GROUP;
  // User must have one of the permissions
  permissions?: AWS_GROUP[];
}

const navLinks: NavItem[] = [
  {
    name: 'Home',
    icon: <HomeIcon color="inherit" />,
    path: '/home',
  },
  {
    name: 'Admin',
    icon: <AdminPanelSettingsIcon />,
    permissions: [
      AWS_GROUP.ARC_GlobalAdmin,
      AWS_GROUP.LDS_GlobalAdmin,
      AWS_GROUP.LDS_KeyingMgr,
    ],
    children: [
      {
        name: 'Subscriptions',
        icon: <DynamicFeedIcon />,
        path: '/admin/subscriptions',
        permissions: [AWS_GROUP.LDS_GlobalAdmin],
      },
      {
        name: 'Inventory Count',
        icon: <FormatListNumberedRtlIcon color="inherit" />,
        path: '/admin/inventory-count',
        permissions: [AWS_GROUP.LDS_GlobalAdmin],
      },
      {
        name: 'Pricing Manager',
        icon: <CurrencyExchangeIcon color="inherit" />,
        path: '/admin/pricing-manager',
        permissions: [AWS_GROUP.LDS_GlobalAdmin],
      },
      {
        name: 'Discounts',
        icon: <DiscountIcon color="inherit" />,
        path: '/admin/discounts',
        permissions: [AWS_GROUP.LDS_GlobalAdmin],
      },
      {
        name: 'Leads Processing',
        icon: <Checklist fontSize="small" />,
        path: '/admin/leads-processing',
        permissions: [AWS_GROUP.ARC_GlobalAdmin, AWS_GROUP.LDS_KeyingMgr],
      },
    ],
  },
  {
    name: 'Team Management',
    icon: <GroupsIcon />,
    path: '/team',
  },
  {
    name: 'Lead Map',
    icon: <TravelExploreIcon color="inherit" />,
    path: '/map',
  },
  {
    name: 'Lead Subscriptions',
    icon: <DynamicFeedIcon color="inherit" />,
    path: '/subscriptions',
  },
  {
    name: 'My Leads',
    icon: <RecentActorsIcon />,
    path: '/leads',
  },
  {
    name: 'Cart History',
    icon: <PaymentsIcon />,
    path: '/history',
  },
  {
    name: 'Weekly Invoices',
    icon: <Receipt />,
    path: '/invoices',
  },
  {
    name: 'Monthly Statements',
    icon: <ReceiptLong />,
    path: '/monthly-statements',
  },
  {
    hideFromProd: true,
    name: 'Lead Resources',
    icon: <FolderSpecialIcon />,
    path: '/resources',
  },
];

// Kept from the original sidebar (the launcher also links to ARC)
const backToArc: NavItem = {
  name: 'Back to ARC',
  icon: <ArrowBackIcon />,
  href: 'https://arc.naaleads.com/',
};

// Sidebar nav colors (design: active row = accent at 12%, icon in the
// darker menu accent)
const NAV_COLORS = {
  text: '#1b1c1d',
  icon: '#00000099',
  activeIcon: '#0070c0',
  activeBg: `${CURRENT_APP.accent}1f`,
  hover: '#0000000a',
};

const SIDEBAR_LINKS = [
  {
    label: 'Terms of service',
    href: 'https://a7d9021b860b18c4222a-d761a6fa6590560fc9f9e4d318d2556e.ssl.cf1.rackcdn.com/contracting/documents/alliance-terms-of-service.pdf',
  },
  {
    label: 'Privacy policy',
    href: 'https://www.integrity.com/privacy-policy',
  },
];

/** Check a nav item's flags against the user's groups */
function useCanView() {
  // Hooks
  const { checkPermissions, hasGroup } = usePermissions();

  return (item: NavItem) => {
    if (item.hideFromProd) {
      return process.env.REACT_APP_AWS_BRANCH !== 'master';
    } else if (item.hasGroup) {
      // User must have this specific group to view link
      return hasGroup(item.hasGroup);
    } else if (item.permissions) {
      // User must have one of the permissions
      return checkPermissions(item.permissions);
    }
    return true;
  };
}

/** One 44px sidebar row: a route link, or a toggle for a group */
function NavRow(props: {
  item: NavItem;
  isActive: boolean;
  nested?: boolean;
  onClick?: () => void;
  trailing?: React.ReactNode;
  expanded?: boolean;
}) {
  // Props
  const { item, isActive } = props;

  let linkProps: object = {
    onClick: props.onClick,
    'aria-expanded': props.expanded,
  };
  if (item.path) {
    linkProps = { component: Link, to: item.path };
  } else if (item.href) {
    linkProps = {
      component: 'a',
      href: item.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  return (
    <ButtonBase
      {...linkProps}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        fontFamily: 'inherit',
        height: props.nested ? 40 : 44,
        width: '100%',
        justifyContent: 'flex-start',
        gap: 2,
        px: '12px',
        pl: props.nested ? '24px' : '12px',
        borderRadius: '10px',
        backgroundColor: isActive ? NAV_COLORS.activeBg : 'transparent',
        color: NAV_COLORS.text,
        fontSize: props.nested ? 14 : 15,
        fontWeight: isActive ? 500 : 400,
        textAlign: 'left',
        '&:hover': {
          backgroundColor: isActive ? NAV_COLORS.activeBg : NAV_COLORS.hover,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 20,
          color: isActive ? NAV_COLORS.activeIcon : NAV_COLORS.icon,
        },
      }}
    >
      {item.icon}
      <Box component="span" sx={{ flex: 1 }}>
        {item.name}
      </Box>
      {props.trailing}
    </ButtonBase>
  );
}

/** A sidebar entry: a link, or a collapsible group (Admin) */
function NavListItem(props: { item: NavItem }) {
  // Props
  const { item } = props;
  // Hooks
  const location = useLocation();
  const canView = useCanView();

  const childActive = Boolean(
    item.children?.some((child) => child.path === location.pathname),
  );
  // State
  const [open, setOpen] = React.useState(childActive);

  React.useEffect(() => {
    // Open the group when navigating to one of its pages
    if (childActive) {
      setOpen(true);
    }
  }, [childActive]);

  if (!canView(item)) {
    return null;
  }

  if (item.children && item.children.length) {
    return (
      <React.Fragment>
        <NavRow
          item={item}
          isActive={false}
          expanded={open}
          onClick={() => setOpen((current) => !current)}
          trailing={
            open ? (
              <ExpandLess sx={{ fontSize: '20px !important' }} />
            ) : (
              <ExpandMore sx={{ fontSize: '20px !important' }} />
            )
          }
        />
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {item.children.filter(canView).map((child) => (
              <NavRow
                key={child.name}
                item={child}
                nested
                isActive={location.pathname === child.path}
              />
            ))}
          </Box>
        </Collapse>
      </React.Fragment>
    );
  }

  return <NavRow item={item} isActive={location.pathname === item.path} />;
}

/**
 * The signed-in shell: one App Bar for every page, the sidebar (permanent at
 * md+, a temporary drawer below), the banner slot, the page outlet, the cart
 * drawer and the session-expired dialog.
 *
 * @param maintenanceEnd - Set while maintenance is active for an admin; shows
 *   the admins-only maintenance banner
 */
export function Dashboard(props: { maintenanceEnd?: string | null }) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  const cartContext = React.useContext(CartContext);
  // Hooks
  const location = useLocation();
  const identity = useAllianceIdentity();
  const { signOut, stopImpersonating, signInAgain } = useSessionActions();

  // The cart belongs to the agent being viewed (impersonated, staff's agent
  // or the user); admins who aren't impersonating have no cart
  const { AgtNo } = useCartAgent();
  // Query - Cart
  const pathCart = '/leads/instant-purchase/cart';
  useQuery({
    queryKey: [pathCart, { AgtNo }],
    // The Leads API only lets Leads admins load another agent's cart, so a
    // 403 here says nothing about seeing that agent's data: keep it out of
    // the noAgent check (it flashed on refresh before the page data loaded)
    meta: { agentAccess: false },
    queryFn: async () => {
      if (isAgt(AgtNo)) {
        const response: {
          data: CartData;
        } = await API.post('LeadsAPI', pathCart, {
          body: { BillToAgt: AgtNo },
        });

        // Add Cart data to Cart Context
        cartContext.dispatch({
          type: 'CART_SET_LEADS',
          payload: response.data,
        });

        return response.data;
      } else {
        cartContext.dispatch({
          type: 'CART_SET_LEADS',
          payload: null,
        });

        return null;
      }
    },
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <AppNavDrawer
        open={state.drawerOpen}
        onClose={() => dispatch({ type: 'DRAWER_CLOSE' })}
      >
        <Box
          sx={{
            p: '6px',
            backgroundColor: SIDEBAR_COLORS.strip,
            borderBottom: '1px solid #0000000f',
            position: 'relative',
            zIndex: 5,
          }}
        >
          <SelectAgentImpersonate />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              flex: 1,
            }}
          >
            {navLinks.map((navItem) => (
              <NavListItem key={navItem.name} item={navItem} />
            ))}
            <NavRow item={backToArc} isActive={false} />
          </Box>

          <Box
            sx={{
              p: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '6px',
            }}
          >
            {SIDEBAR_LINKS.map((link) => (
              <Box
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  color: '#00000080',
                  transition: 'color 0.2s',
                  '&:hover': { color: NAV_COLORS.activeIcon },
                }}
              >
                {link.label}
              </Box>
            ))}
          </Box>
        </Box>
      </AppNavDrawer>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        }}
      >
        <AllianceAppBar
          title={state.routeTitle}
          sidebarWidth={SIDEBAR_WIDTH}
          loading={state.routeLoading}
          onMenuClick={() => dispatch({ type: 'TOGGLE_DRAWER' })}
          menuOpen={state.drawerOpen}
          actions={<CartButton />}
          user={
            identity ? (
              <UserControl
                identity={identity}
                onSignOut={signOut}
                onStopImpersonating={stopImpersonating}
              />
            ) : undefined
          }
        />

        <Box component="main">
          <AllianceAppBarSpacer />
          <AppBanners />
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </Box>
      </Box>

      <Cart />

      {/* Banner sources: each keeps its banner in the queue while it applies */}
      <ConnectionBanner />
      <UpdatedBanner pathname={location.pathname} />
      {props.maintenanceEnd ? (
        <MaintenanceBanner until={props.maintenanceEnd} onSignOut={signOut} />
      ) : null}

      <SessionExpiredDialog
        open={authContext.state.tokenError}
        onSignIn={signInAgain}
        onSignOut={signOut}
      />
    </Box>
  );
}
